// apps/web/src/data/query-keys.ts
// Hierarchical query-key catalog per Plan §4.6.8.
// Pattern: [<family>, <sub-resource>, <id-or-filter>] — supports
// TanStack Query partial-match invalidation (e.g., invalidate all 'brief'
// keys after a chain run).
//
// SINGLE SOURCE OF TRUTH for every TanStack queryKey in apps/web. Hooks in
// data/queries/*.ts call these factories; tests in **/__snap__ + **/*.test.tsx
// seed cache entries via the same factories. Inline tuple literals are
// banned — see scripts/check-query-key-catalog.js (audit-r3 F-007 close-out).
//
// Default param values are baked in here so hook + test keys match without
// the caller having to remember to pass the same default twice. If a hook's
// default changes, change it BOTH in the hook AND in the factory below.

import type { ThresholdSet, CalibrationSource } from '@cf-apps/shared';

export const QK = {
  // ── BRIEF / MACRO ─────────────────────────────────────────────────────
  brief: {
    latest:  (sparklineHours: number)            => ['brief', 'latest',  { sparklineHours }] as const,
    byId:    (id: string, sparklineHours: number) => ['brief', 'id', id, { sparklineHours }] as const,
    history: (limit: number)                     => ['brief', 'history', { limit }] as const,
    recent:  (limit: number, includeFailed: boolean) => ['brief', 'recent', { limit, includeFailed }] as const,
    newsImpact: (id: string)                     => ['brief', 'news-impact', id] as const,
  },
  // Chain V2 (Phase 6 §E cutover — 2026-05-25): V2 payload now embedded in
  // BriefBundle.v2, served by /api/brief. The separate briefV2 cache namespace
  // was removed; consumers read bundle.v2 directly from the existing brief
  // query keys above.
  macro: {
    history: (days: number) => ['macro', 'history', { days }] as const,
  },

  // ── ECON CALENDAR (MARKET ANALYSIS QUANTIFICATION timeline, 2026-06-10) ─
  econCalendar: {
    upcoming: () => ['econ-calendar', 'upcoming'] as const,
  },

  // ── OPS ───────────────────────────────────────────────────────────────
  ops: {
    status:           ()                       => ['ops', 'status']                                    as const,
    schedule:         ()                       => ['ops', 'schedule']                                  as const,
    cronTimeline:     (hours: number)          => ['ops', 'health', 'cron-timeline',     { hours }]   as const,
    stageFailures:    (runs: number)           => ['ops', 'health', 'stage-failures',    { runs }]    as const,
    subrequestBudget: (briefId: string | null) => ['ops', 'health', 'subrequest-budget', briefId ?? ''] as const,
    llmCalls:         (briefId: string | null) => ['ops', 'health', 'llm-calls', briefId ?? '']        as const,
    refreshFanout:    ()                       => ['ops', 'health', 'refresh-fanout']                 as const,
    failureBreakdown: (hours: number)          => ['ops', 'health', 'failure-breakdown', hours]       as const,
    lhtStatus:        (hours: number)          => ['ops', 'health', 'lht-status',        hours]       as const,
    registry:         (days: number)           => ['ops', 'registry', days]                           as const,
    oracle:           (hours: number, runs: number) => ['ops', 'oracle', { hours, runs }]             as const,
    oracleMacro:      (hours: number, runs: number) => ['ops', 'oracle-macro', { hours, runs }]       as const,
    oracleRelations:  (days: number)                => ['ops', 'oracle-relations', { days }]          as const,
    oracleRelationsConfig: ()                       => ['ops', 'oracle-relations-config']             as const,
    oracleFeedsConfig: ()                           => ['ops', 'oracle-feeds-config']                 as const,
    oracleRegistry:   ()                            => ['ops', 'oracle-registry']                     as const,
    oracleLineage:    ()                            => ['ops', 'oracle-lineage']                      as const,
    oracleNewsPolicy: ()                            => ['ops', 'oracle-news-policy']                  as const,
    oracleNews:       (hours: number)               => ['ops', 'oracle-news', { hours }]              as const,
    oracleCacheStats: ()                            => ['ops', 'oracle-cache-stats']                  as const,
    oracleConfigHistory: (entity: 'source' | 'policy' | 'relation' | 'feed' | 'news_policy', entityId: string) =>
      ['ops', 'oracle-config-history', entity, entityId] as const,
  },

  // ── NOTIFICATIONS (Ops Notices — global bell, 2026-07-05) ─────────────
  notifications: {
    list: () => ['notifications', 'list'] as const,
  },

  // ── AMM ───────────────────────────────────────────────────────────────
  amm: {
    all:             ()                                          => ['amm']                                             as const,
    latest:          ()                                          => ['amm', 'latest']                                   as const,
    history:         (days: number)                              => ['amm', 'history',  { days }]                      as const,
    positionHistory: (nftId: string | null, days: number)        => ['amm', 'positions', 'history', { nftId, days }]   as const,
  },

  // ── ACCOUNT (Holdings & Value — AdAmm zone, 2026-06-04) ───────────────
  // baselineDate null = operator-default ACCOUNT_BASELINE_DATE; a non-null
  // value is the UI date-picker override (affects read-time deltas only, so
  // it's part of the cache key on both latest + history).
  account: {
    all:     ()                                          => ['account'] as const,
    latest:  (baselineDate: string | null)               => ['account', 'latest',  baselineDate ?? ''] as const,
    history: (days: number, baselineDate: string | null) => ['account', 'history', { days, baselineDate: baselineDate ?? '' }] as const,
  },

  // ── BITCOIN (holdings — AdAmm zone, 2026-06-27) ──────────────────────
  bitcoin: {
    all:    ()                                          => ['bitcoin']           as const,
    latest: ()                                          => ['bitcoin', 'latest']  as const,
  },

  // ── TRACKER / SIGNAL ──────────────────────────────────────────────────
  tracker: {
    tickers: ()                                          => ['tracker', 'tickers']                          as const,
    view:    (ticker: string, days: number)              => ['tracker', 'ticker',  ticker,  { days }]      as const,
    compare: (tickers: ReadonlyArray<string>, days: number) =>
      ['tracker', 'compare', [...tickers].sort(), { days }] as const,
  },
  signal: {
    drill: (id: string | null) => ['signal', 'id', id ?? ''] as const,
  },

  // ── CALIBRATION ───────────────────────────────────────────────────────
  // 2026-06-16 — Q&A calibration source toggle (D3): K1-K4 keys now carry
  // `source` (signals|qa|combined) so cache entries don't collide across the
  // segmented control. accuracy-grid-history (K2 hover) stays signals-scoped
  // (hover sparkline is a per-signal-ticker detail, not source-toggled).
  calibration: {
    snapshot:            (days: number)                                                            => ['calibration', 'snapshot',              { days }]                                       as const,
    reliability:         (days: number, scorerVersion: string | null, source: CalibrationSource)   => ['calibration', 'reliability',           { days, sv: scorerVersion ?? '', source }]      as const,
    accuracyGrid:        (scorerVersion: string | null, source: CalibrationSource)                 => ['calibration', 'accuracy-grid',         { sv: scorerVersion ?? '', source }]            as const,
    accuracyHistory:     (
      ticker:        string | null,
      horizon:       string | null,
      days:          number,
      scorerVersion: string | null,
    ) => ['calibration', 'accuracy-grid-history', { ticker, horizon, days, sv: scorerVersion ?? '' }] as const,
    brierTrend:          (days: number, source: CalibrationSource)                                  => ['calibration', 'brier-trend',           { days, source }]                               as const,
    thresholdProjection: (thresholds: ThresholdSet, days: number, source: CalibrationSource)        => ['calibration', 'threshold-projection',  { ...thresholds, days, source }]                as const,
    triage:              (limit: number)                                                           => ['calibration', 'triage',                { limit }]                                      as const,
    // ── Q&A recurring-question section (K7 + K8) ────────────────────────────
    qaQuestions:         ()                                                                        => ['calibration', 'qa-questions']                                                          as const,
    qaQuestionTrend:     (questionId: string, days: number)                                        => ['calibration', 'qa-question-trend', questionId, { days }]                               as const,
    qaInputWeighting:    (questionId: string | null, includeArchived: boolean)                     => ['calibration', 'qa-input-weighting', questionId ?? '', { includeArchived }]             as const,
    // ── p.arbiter Pro-vs-Flash shadow A/B (K10, 2026-06-20) ─────────────────
    arbiterAB:           ()                                                                        => ['calibration', 'arbiter-ab']                                                            as const,
    // ── DeepSeek Flash non-think A/B (ca#2, 2026-08-14) ─────────────────────
    nonthinkAB:          ()                                                                        => ['calibration', 'nonthink-ab']                                                           as const,
    // ── Crypto Rank IC (K11) + Target Calibration (K12), 2026-06-25 ──────────
    rankIcBaskets:       ()                                                                        => ['calibration', 'rank-ic-baskets']                                                       as const,
    rankIcCycles:        (basketId: string)                                                       => ['calibration', 'rank-ic-cycles', basketId]                                             as const,
    rankIcBrief:         (briefId: string)                                                        => ['calibration', 'rank-ic-brief', briefId]                                                as const,
    targetCalibration:   ()                                                                        => ['calibration', 'target-calibration']                                                    as const,
  },

  // ── SYNTHESIS QUALITY ─────────────────────────────────────────────────
  synthesis: {
    qualityHistory: (days: number) => ['synthesis', 'quality-history', { days }] as const,
    qualityLatest:  ()             => ['synthesis', 'quality-latest']           as const,
  },

  // ── LONG-HORIZON THESIS (Thesis Card, 2026-07-09) ─────────────────────
  lht: {
    bundle: () => ['lht', 'bundle'] as const,
  },

  // ── COSTS ─────────────────────────────────────────────────────────────
  costs: {
    today:   ()                       => ['costs', 'today']                                  as const,
    trend:   (days: number)           => ['costs', 'trend',    { days }]                     as const,
    byStage: (briefId: string | null) => ['costs', 'by-stage', briefId ?? '']                as const,
    latency: (briefId: string | null) => ['costs', 'latency',  briefId ?? '']                as const,
    monthly: ()                       => ['costs', 'monthly']                                as const,
  },

  // ── INVARIANTS ────────────────────────────────────────────────────────
  invariants: {
    root: () => ['invariants'] as const,
  },

  // ── QA (Q-stage substrate per AS-4) ───────────────────────────────────
  qa: {
    questions: ()             => ['qa', 'questions'] as const,
    archived:  ()             => ['qa', 'archived']  as const,
    status:    ()             => ['qa', 'status']    as const,
    runs:      ()             => ['qa', 'runs']      as const,
  },

  // ── RUNTIME PARAMS (OP-PARAMS tab, 2026-05-27) ────────────────────────
  params: {
    list:    ()          => ['params', 'list']           as const,
    history: (key: string) => ['params', 'history', key] as const,
  },

  // ── RUNS (Operations Runs tab, Session 5Q 2026-05-27) ─────────────────
  // Partial-match invalidation: useRunsStream invalidates ['runs'] on every
  // WS event so list/active/detail queries all re-fetch.
  runs: {
    all:      ()                                          => ['runs'] as const,
    active:   ()                                          => ['runs', 'active']                 as const,
    list:     (filters: RunsListFilters)                  => ['runs', 'list', filters]          as const,
    detail:   (id: string)                                => ['runs', 'detail', id]             as const,
    params:   (id: string)                                => ['runs', 'params', id]             as const,
    schedule: ()                                          => ['runs', 'schedule']               as const,
  },
} as const;

// ─── Runs filter shape (canonical) ─────────────────────────────────────────
// Hoisted here so query-key catalog + hooks share the SAME filter object
// reference. Tests must pass equivalent filters by value (deep-equal) to
// hit the same cache entry.
export interface RunsListFilters {
  /** Scheduler filter; null = all schedulers. */
  scheduler: string | null;
  /** Status filter; null = all statuses. */
  status:    string | null;
  /** ISO date (inclusive) lower bound; null = no lower bound. */
  from:      string | null;
  /** ISO date (inclusive) upper bound; null = no upper bound. */
  to:        string | null;
  /** Page size — server caps at 200 per Plan §runs. */
  limit:     number;
  /** Cursor for keyset pagination; null = first page. */
  cursor:    string | null;
}
